// Guardas de rota só do painel (vieram de guardas.tsx; mesma regra, ver o cabeçalho de lá). Ficam à parte porque
// dependem do menu do painel (navegacao.ts) e só rotas.painel.tsx os usa: assim o build do app nativo, que não
// tem o painel, também não carrega o menu (ADR app nativo, D6).
import type { ReactNode } from "react";
import type { Permissao } from "@grindset/server/contratos";
import { Navigate, Outlet } from "react-router";
import { ehEquipe, useSessao } from "./api/sessao";
import { BotaoLink, Icones, SemPermissao, Vazio } from "./compartilhado/ui";
import { ContaSemArea, ErroSessao, Splash, useVolta } from "./guardas";
import { usePapel } from "./painel/comum/papel";
import { primeiroDestino } from "./painel/navegacao";

/** Painel: exige papel da equipe. Senha temporária -> /painel/trocar-senha. */
export function ExigirEquipe({
  permitirTrocaSenha = false,
  children,
}: {
  permitirTrocaSenha?: boolean;
  children?: ReactNode;
}) {
  const { data: sessao, isPending, isError, refetch } = useSessao();
  const volta = useVolta();
  if (isPending) return <Splash />;
  if (isError) return <ErroSessao aoTentar={() => void refetch()} />;
  if (!sessao) return <Navigate to={`/painel/entrar?volta=${volta}`} replace />;
  if (!ehEquipe(sessao.usuario.papel))
    return sessao.usuario.papel === "ALUNO" ? <Navigate to="/" replace /> : <ContaSemArea />;
  if (sessao.usuario.trocarSenha && !permitirTrocaSenha)
    return <Navigate to="/painel/trocar-senha" replace />;
  return <>{children ?? <Outlet />}</>;
}

/**
 * Tela que exige permissões (docs/seguranca/RBAC.md): sem elas, o estado "sem permissão" (DESIGN §6), nunca
 * dado parcial. `inicio`: em vez do aviso, manda para a primeira tela permitida (ex.: recepção no /painel).
 */
export function ExigirPermissao({
  permissoes,
  qualquer,
  inicio = false,
  children,
}: {
  permissoes: readonly Permissao[];
  /** Basta uma destas (além de todas as de `permissoes`), como `DestinoPainel.qualquer`. */
  qualquer?: readonly Permissao[];
  inicio?: boolean;
  children?: ReactNode;
}) {
  const { pode } = usePapel();
  if (!permissoes.every(pode) || (qualquer && !qualquer.some(pode))) {
    if (inicio) {
      const destino = primeiroDestino(pode);
      if (destino) return <Navigate to={destino} replace />;
      return (
        <div className="px-margem py-10 lg:px-0" data-sem-telas="">
          <Vazio
            icone={Icones.bloqueado}
            titulo="Você ainda não tem acesso a nenhuma tela"
            texto="Fale com o gerente para liberar o que você usa no dia a dia."
          />
        </div>
      );
    }
    return (
      <div className="px-margem py-10 lg:px-0">
        <SemPermissao
          acao={
            <BotaoLink to="/painel" variante="secundario">
              Voltar para o início
            </BotaoLink>
          }
        />
      </div>
    );
  }
  return <>{children ?? <Outlet />}</>;
}
